import { post } from './api';
import type { UploadUrlResponse } from '../types/extraction';

export async function requestUploadUrl(
  fileExtension: string = '.jpg',
  contentType: string = 'image/jpeg'
): Promise<UploadUrlResponse> {
  return post<UploadUrlResponse>('/api/uploads/roster-image', {
    fileExtension,
    contentType,
  });
}

export async function uploadToGCS(
  file: File,
  uploadUrl: string
): Promise<void> {
  // Note: We intentionally don't set Content-Type header here to avoid CORS preflight.
  // The backend includes Content-Type in the signed URL parameters, so GCS will
  // still validate the content type correctly.

  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
    });

    if (!response.ok) {
      throw new Error(`GCS upload failed: ${response.statusText}`);
    }
  } catch (error) {
    // Check if it's a network error (likely CORS)
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error(
        'Upload failed due to network error. This may be a CORS configuration issue. ' +
        'Please try again or contact support if the problem persists.'
      );
    }

    // Re-throw other errors
    throw error;
  }
}

export async function uploadRosterFile(
  file: File
): Promise<{ gcsUrl: string; imageId: string }> {
  // Step 1: Request signed URL
  const extension = `.${file.name.split('.').pop()}`;
  const { uploadUrl, gcsUrl, imageId } = await requestUploadUrl(extension, file.type);

  // Step 2: Upload to GCS
  await uploadToGCS(file, uploadUrl);

  // Step 3: Return the permanent GCS URL plus the originating UploadMetadata id.
  // `imageId` is threaded through the wizard so a team created from this upload
  // can be linked to its roster image (see CreateTeamWithPlayersRequest.rosterUploadId).
  return { gcsUrl, imageId };
}
